import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { apiErrorMessage } from '../apiError';

// Plain-English names for the actions recorded by the backend (activity_log.py)
const ACTION_LABELS = {
  sign_in: 'Signed in',
  view_filing: 'Viewed filing',
  view_excise: 'Viewed abatement',
  download_document: 'Downloaded document',
  export_filings: 'Exported to Excel',
  set_filing_status: 'Changed filing status',
  set_excise_status: 'Changed abatement status',
  message_filer: 'Emailed filer',
  delete_filing: 'Deleted filing',
  delete_excise: 'Deleted abatement',
  create_admin: 'Created admin',
  promote_user: 'Promoted user to admin',
  delete_user: 'Deleted user',
  update_town: 'Edited town',
};

const PAGE_SIZE = 100;
const EMPTY_FILTERS = { admin: '', action: '', town: '', from: '', to: '' };

// Super-admin view of the admin activity log: who did what, newest first.
function ActivityLog({ token }) {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [entries, setEntries] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = async (activeFilters, offset) => {
    setLoading(true);
    setError('');
    const params = { limit: PAGE_SIZE, offset };
    if (activeFilters.admin) params.admin_email = activeFilters.admin;
    if (activeFilters.action) params.action = activeFilters.action;
    if (activeFilters.town) params.town_code = activeFilters.town;
    // whole days in the viewer's own time zone
    if (activeFilters.from) params.start = new Date(`${activeFilters.from}T00:00:00`).toISOString();
    if (activeFilters.to) {
      const end = new Date(`${activeFilters.to}T00:00:00`);
      end.setDate(end.getDate() + 1);
      params.end = end.toISOString();
    }
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/admin/activity`, {
        params,
        headers: { Authorization: `Bearer ${token}` },
      });
      setEntries(prev => (offset === 0 ? res.data.entries : [...prev, ...res.data.entries]));
      setHasMore(res.data.has_more);
    } catch (err) {
      setError(apiErrorMessage(err, 'Could not load activity.'));
    } finally {
      setLoading(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(EMPTY_FILTERS, 0); }, []);

  const set = (key) => (e) => setFilters({ ...filters, [key]: e.target.value });
  const clear = () => { setFilters(EMPTY_FILTERS); load(EMPTY_FILTERS, 0); };

  return (
    <div>
      <div style={styles.filterRow}>
        <input style={styles.input} placeholder="Admin email" value={filters.admin} onChange={set('admin')} />
        <select style={styles.input} value={filters.action} onChange={set('action')}>
          <option value="">All actions</option>
          {Object.entries(ACTION_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
        <input style={{ ...styles.input, minWidth: '90px', width: '90px' }} placeholder="Town code" value={filters.town} onChange={set('town')} />
        <label style={styles.dateLabel}>From <input style={styles.input} type="date" value={filters.from} onChange={set('from')} /></label>
        <label style={styles.dateLabel}>To <input style={styles.input} type="date" value={filters.to} onChange={set('to')} /></label>
        <button style={styles.button} onClick={() => load(filters, 0)}>Filter</button>
        <button style={styles.clearButton} onClick={clear}>Clear</button>
      </div>

      {error && <p style={styles.error}>{error}</p>}

      {entries.length === 0 && !loading ? (
        <p style={styles.muted}>No activity found.</p>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              {['When', 'Admin', 'Action', 'Record', 'Town', 'Details', 'IP Address'].map(h => <th key={h} style={styles.th}>{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {entries.map((e, i) => (
              <tr key={e.id} style={{ backgroundColor: i % 2 === 0 ? '#f8fafc' : '#ffffff' }}>
                <td style={styles.td}>{e.created_at ? new Date(e.created_at).toLocaleString() : ''}</td>
                <td style={styles.td}>{e.admin_email}</td>
                <td style={styles.td}>{ACTION_LABELS[e.action] || e.action}</td>
                <td style={styles.td}>{e.record_reference || ''}</td>
                <td style={styles.td}>{e.town_code || ''}</td>
                <td style={styles.td}>{e.detail || ''}</td>
                <td style={{ ...styles.td, color: '#888', fontSize: '12px' }}>{e.ip_address || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {loading && <p style={styles.muted}>Loading...</p>}
      {hasMore && !loading && (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <button style={styles.button} onClick={() => load(filters, entries.length)}>Load more</button>
        </div>
      )}
    </div>
  );
}

// same look as the other dashboard tabs
const styles = {
  filterRow: { display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap', alignItems: 'center' },
  input: { padding: '8px 10px', borderRadius: '4px', border: '1px solid #ccc', fontSize: '14px', minWidth: '140px' },
  dateLabel: { fontSize: '14px', color: '#555', display: 'flex', alignItems: 'center', gap: '6px' },
  button: { padding: '8px 16px', backgroundColor: '#1a3a5c', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  clearButton: { padding: '8px 16px', backgroundColor: '#888', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '14px' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '14px' },
  th: { backgroundColor: '#1a3a5c', color: '#ffffff', padding: '10px 12px', textAlign: 'left', fontWeight: '600' },
  td: { padding: '10px 12px', borderBottom: '1px solid #eee', color: '#333' },
  error: { color: '#cc0000', fontSize: '14px', marginBottom: '16px' },
  muted: { textAlign: 'center', color: '#666', padding: '24px' },
};

export default ActivityLog;
